'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, cloneElement, forwardRef, isValidElement, useEffect, useRef, useState } from "react";
import { Button } from "../Button/index.js";
import { CaptionedMedia } from "../CaptionedMedia/index.js";
import { Icon } from "../Icon/index.js";
import { Typography } from "../Typography/index.js";
import useScreenMediaQuery from "../../common/hooks/useScreenMediaQuery.js";
/**
 * Media carousel - [API](https://scl.sharp.com/?path=/docs/mediacarousel--basic) | [DSM](https://sharphealthcare.notion.site/Media-carousel-410278b095434d33aee347469ef6d5f4)
 */ const MediaCarousel = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { as, onSlideChange, children } = props, rest = _object_without_properties(props, [
        "as",
        "onSlideChange",
        "children"
    ]);
    const [containerWidth, setContainerWidth] = useState(0);
    const sliderRef = useRef(null);
    const [disableButton, setDisableButton] = useState({
        left: true,
        right: false
    });
    const Root = as !== null && as !== void 0 ? as : 'div';
    const isDesktop = useScreenMediaQuery('md');
    const slide = (direction)=>{
        if (!sliderRef.current) return;
        const { scrollLeft, clientWidth } = sliderRef.current;
        const left = direction === 'next' ? scrollLeft + clientWidth : scrollLeft - clientWidth;
        sliderRef.current.scroll({
            left,
            behavior: 'smooth'
        });
    };
    const updateButtonsVisibility = ()=>{
        if (!sliderRef.current) return;
        const { scrollLeft, clientWidth, scrollWidth } = sliderRef.current;
        setDisableButton({
            left: scrollLeft === 0 || !scrollLeft,
            right: Math.round(scrollLeft + clientWidth) >= scrollWidth
        });
    };
    useEffect(()=>{
        const resizeObserver = new ResizeObserver((entries)=>{
            setContainerWidth(entries[0].contentRect.width);
        });
        if (sliderRef.current) {
            resizeObserver.observe(sliderRef.current);
        }
        return ()=>{
            if (sliderRef.current) {
                resizeObserver.unobserve(sliderRef.current);
            }
        };
    }, []);
    useEffect(()=>{
        if ((sliderRef === null || sliderRef === void 0 ? void 0 : sliderRef.current) && onSlideChange) {
            sliderRef.current.onscrollend = (e)=>{
                onSlideChange(e);
            };
        }
    }, []);
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({
        className: "w-full h-full overflow-hidden md:relative md:z-10 rounded-xl",
        ref: ref
    }, rest), {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex md:relative md:z-10 scroll-smooth snap-x snap-mandatory md:gap-5 overflow-x-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] space-x-5 md:space-x-0",
                ref: sliderRef,
                onScroll: updateButtonsVisibility,
                // eslint-disable-next-line
                tabIndex: 0,
                children: Children.map(children, (child, index)=>{
                    if (/*#__PURE__*/ isValidElement(child) && !!child.props.media) {
                        return /*#__PURE__*/ cloneElement(child, {
                            activeSlide: index + 1,
                            amountOfSlides: Children.count(children)
                        });
                    }
                    throw new Error('Media carousel children must be Slide components');
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-end md:justify-between gap-4 mt-5 md:mt-0 md:px-5 md:w-full md:absolute md:z-50 md:-translate-y-1/2",
                style: {
                    top: `calc((${containerWidth}px * (9 / 16)) / 2)`
                },
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        className: "opacity-100 before:border before:hover:drop-shadow-none",
                        disabled: disableButton.left,
                        color: "light",
                        size: isDesktop ? 'sm' : 'xs',
                        shape: "circle",
                        onClick: ()=>slide('prev'),
                        children: /*#__PURE__*/ _jsx(Icon, {
                            title: "Previous slide",
                            size: isDesktop ? 'lg' : 'sm',
                            icon: "chevron-left",
                            className: disableButton.left ? 'text-gray-200' : undefined
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        className: "opacity-100 before:border before:hover:drop-shadow-none",
                        disabled: disableButton.right,
                        color: "light",
                        size: isDesktop ? 'sm' : 'xs',
                        shape: "circle",
                        onClick: ()=>slide('next'),
                        children: /*#__PURE__*/ _jsx(Icon, {
                            title: "Next slide",
                            size: isDesktop ? 'lg' : 'sm',
                            icon: "chevron-right",
                            className: disableButton.right ? 'text-gray-200' : undefined
                        })
                    })
                ]
            })
        ]
    }));
});
function InternalSlide(props) {
    const { media, caption, activeSlide, amountOfSlides } = props;
    let concatCaption;
    if (caption) {
        concatCaption = /*#__PURE__*/ _jsxs(Typography, {
            variant: "small-body",
            className: "mt-3",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    className: "font-bold",
                    children: `${activeSlide} of ${amountOfSlides}: `
                }),
                caption
            ]
        });
    }
    return /*#__PURE__*/ _jsx(CaptionedMedia, {
        media: media,
        caption: concatCaption,
        rounded: true,
        className: "shrink-0 w-10/12 md:w-full h-full snap-center flex flex-col"
    });
}
function Slide(props) {
    return /*#__PURE__*/ _jsx(InternalSlide, _object_spread({}, props));
}
export { MediaCarousel, Slide };
